import React, { useMemo } from "react";
import { Check, ChevronDown } from "lucide-react";
import { BADGE_CONFIG, getStatusIcon } from "./DealStatusBadge";
import { useBorrowerDetail } from "@/hooks/useBorrowerDetail";
import { useAppStatusTransitions } from "@/hooks/queries";
import { AppStatus } from "@/types/borrower";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { cn } from "@/lib/utils";

interface StatusFlowbarProps {
  borrowerId: number;
  editable?: boolean;
  /** Hide the left status badge tile (use when badge is shown inline in the page header) */
  hideBadge?: boolean;
}

const CIRCLE_SIZE = 16;

/** Five milestones on the bar (matches product flow). */
const MILESTONE_LABELS = [
  "Submitted",
  "Funder Review",
  "Contract Signed",
  "Final Underwriting",
  "Funded",
] as const;

/** Float progress 0–4 along milestones; -1 = declined (no path progress). */
function getProgressIndex(status: AppStatus): number {
  const map: Partial<Record<AppStatus, number>> = {
    submitted: 0,
    ai_analyzing: 0.5,
    funder_review: 1,
    contract_ready: 1.4,
    contract_sent: 1.78,
    contract_signed: 2,
    bank_verification_required: 2.2,
    plaid_initiated: 2.4,
    plaid_completed: 2.78,
    final_underwriting: 3,
    funded: 4,
    repayment: 4,
    paid_off: 4,
    defaulted: 4,
    refi_ready: 4,
    declined: -1,
    failed: -1,
  };
  return map[status] ?? 0;
}

function segmentSubLabel(status: AppStatus): string | null {
  switch (status) {
    case "ai_analyzing":
      return "AI Analyzing";
    case "contract_ready":
      return "Contract Ready";
    case "contract_sent":
      return "Contract Sent";
    case "bank_verification_required":
      return "Bank Verification";
    case "plaid_initiated":
      return "Plaid Started";
    case "plaid_completed":
      return "Plaid Complete";
    default:
      return null;
  }
}

/** Segment index 0..3 is the connector after milestone `index` (before milestone index+1). */
function subLabelSegmentIndex(status: AppStatus): number | null {
  if (status === "ai_analyzing") return 0;
  if (status === "contract_ready" || status === "contract_sent") return 1;
  if (
    status === "bank_verification_required" ||
    status === "plaid_initiated" ||
    status === "plaid_completed"
  ) {
    return 2;
  }
  return null;
}

const StatusFlowbar: React.FC<StatusFlowbarProps> = ({
  borrowerId,
  editable = false,
  hideBadge = false,
}) => {
  const { borrower, updating, patch } = useBorrowerDetail(borrowerId);
  const { choices, transitions, manualPatchBlocked } = useAppStatusTransitions();

  const blockedSet = useMemo(() => new Set(manualPatchBlocked), [manualPatchBlocked]);

  if (!borrower) return null;

  const currStatus = borrower.app_status;
  const isDeclined = currStatus === "declined" || currStatus === "failed";
  const validTransitions = (transitions[currStatus] ?? []).filter((s) => !blockedSet.has(s));

  const progressIndex = getProgressIndex(currStatus);
  const subLabel = segmentSubLabel(currStatus);
  const subSegIdx = subLabelSegmentIndex(currStatus);

  const isClickable = editable && !updating;

  const handleStatusSelect = async (newStatus: AppStatus) => {
    try {
      await patch({ app_status: newStatus });
    } catch (err) {
      void err;
    }
  };

  const getChoiceLabel = (value: string) => choices.find((c) => c.value === value)?.label ?? value;

  const badgeConfig = BADGE_CONFIG[currStatus];
  const statusColor = badgeConfig?.color ?? "hsl(var(--primary))";
  // Track uses the status color (green for funded, red for declined, blue for in-progress)
  const trackColor = isDeclined ? "#bfbebe" : statusColor;

  // ── Status Badge (left tile) ─────────────────────────────────────
  const statusBadge = (
    <button
      type="button"
      disabled={!isClickable || validTransitions.length === 0}
      className={cn(
        "relative flex w-[82px] shrink-0 flex-col items-center justify-center gap-1 rounded-lg px-2 py-1.5",
        "select-none border-0 text-white shadow-sm transition-all duration-200 focus:outline-none",
        isClickable && validTransitions.length > 0
          ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-md active:scale-[0.97]"
          : "cursor-default"
      )}
      style={{
        backgroundColor: isDeclined ? "#eb6b6b95" : `${statusColor}45`,
        border: `2px solid ${statusColor}33`,
      }}
    >
      {/* Icon circle */}
      <div
        className="flex h-5 w-5 items-center justify-center rounded-full"
        style={{ backgroundColor: `${statusColor}` }}
      >
        {getStatusIcon(currStatus, "h-3 w-3 text-white")}
      </div>
      {/* Labels */}
      <div className="flex flex-col items-center gap-0 text-center">
        <span
          className="text-[6px] font-semibold uppercase tracking-widest"
          style={{ color: `${statusColor}` }}
        >
          Status
        </span>
        <span
          className="text-[9px] font-bold capitalize leading-tight text-white"
          style={{ color: `${statusColor}` }}
        >
          {badgeConfig?.label ?? currStatus}
        </span>
      </div>
      {/* Chevron edit hint */}
      {isClickable && validTransitions.length > 0 && (
        <ChevronDown
          className="absolute bottom-0.5 right-1 h-2.5 w-2.5"
          style={{ color: `${statusColor}` }}
        />
      )}
    </button>
  );

  return (
    <div className="mb-1 flex flex-row items-stretch gap-2">
      {/* ── Badge ── */}
      {!hideBadge &&
        (editable && !updating ? (
          <ContextMenu>
            <ContextMenuTrigger asChild>
              <div className="shrink-0">
                <DropdownMenu>
                  <DropdownMenuTrigger
                    asChild
                    disabled={!isClickable || validTransitions.length === 0}
                  >
                    {statusBadge}
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[180px]">
                    {validTransitions.map((status) => (
                      <DropdownMenuItem key={status} onClick={() => handleStatusSelect(status)}>
                        {getChoiceLabel(status)}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </ContextMenuTrigger>
            <ContextMenuContent className="min-w-[180px]">
              {choices
                .filter((c) => c.value !== currStatus && !blockedSet.has(c.value))
                .map((c) => (
                  <ContextMenuItem key={c.value} onSelect={() => void handleStatusSelect(c.value)}>
                    {c.label}
                  </ContextMenuItem>
                ))}
            </ContextMenuContent>
          </ContextMenu>
        ) : (
          <div className="shrink-0">{statusBadge}</div>
        ))}

      {/* ── Progress Track ── */}
      <div className="flex flex-1 flex-col justify-center rounded-lg border border-border/50 bg-card px-7 pb-1 pt-2.5 shadow-sm">
        {/* Nodes + connectors */}
        <div className="flex items-center">
          {MILESTONE_LABELS.map((_, idx) => {
            const isLast = idx === MILESTONE_LABELS.length - 1;
            // nodePast: strictly passed this milestone (complete ✓)
            const nodePast = !isDeclined && progressIndex > idx;
            // nodeActive: at this milestone, but only when NOT in a sub-state (sub-states pulse on their dot, not the parent node)
            const nodeActive =
              !isDeclined && progressIndex >= idx && progressIndex < idx + 1 && !subLabel;
            // isSubSegment: this connector is the one currently showing a sub-state
            // (e.g. "Contract Sent", "Plaid Started"). Its dot and fill always sit
            // at the midpoint of the segment rather than tracking the exact
            // fractional progress, so the fill visually "meets" the dot.
            const isSubSegment = !isDeclined && !!subLabel && subSegIdx === idx;
            const connFill = isDeclined ? 0 : Math.min(Math.max(progressIndex - idx, 0), 1);
            const fillFraction = isSubSegment ? 0.5 : connFill;

            return (
              <React.Fragment key={idx}>
                {/* Node */}
                <div
                  className="relative z-[1] shrink-0"
                  style={{ width: CIRCLE_SIZE, height: CIRCLE_SIZE }}
                >
                  {/* Circle */}
                  <div
                    className={cn(
                      "absolute inset-0 flex items-center justify-center rounded-full border-2 transition-all duration-300",
                      !nodePast && !nodeActive && "border-border/50 bg-background"
                    )}
                    style={
                      nodePast
                        ? { backgroundColor: trackColor, borderColor: trackColor }
                        : nodeActive
                          ? { borderColor: trackColor, backgroundColor: "white" }
                          : {}
                    }
                  >
                    {nodePast ? (
                      <Check className="h-2.5 w-2.5 text-white" />
                    ) : nodeActive ? (
                      <div
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: trackColor }}
                      />
                    ) : (
                      <div className="h-1 w-1 rounded-full bg-border/50" />
                    )}
                  </div>
                </div>

                {/* Connector */}
                {!isLast && (
                  <div className="relative z-0 mx-[-8px] flex-1" style={{ height: CIRCLE_SIZE }}>
                    {/* Inactive rail */}
                    <div className="absolute left-0 right-0 top-1/2 h-[1.5px] -translate-y-1/2 rounded-full bg-border/40" />
                    {/* Active fill */}
                    <div
                      className="absolute left-0 top-1/2 h-[1.5px] -translate-y-1/2 rounded-full transition-[width] duration-500 ease-spring"
                      style={{ backgroundColor: trackColor, width: `${fillFraction * 100}%` }}
                    />
                    {/* Sub-state dot: always centered on the segment, independent of
                        exact progress within the sub-state. */}
                    {isSubSegment && (
                      <div
                        className="absolute z-[2]"
                        style={{
                          left: "50%",
                          top: "50%",
                          transform: "translate(-50%, -50%)",
                        }}
                      >
                        {/* Ping halo (only scales, no own positioning) */}
                        <span
                          className="absolute inset-0 animate-ping rounded-full bg-current opacity-25"
                          style={{
                            backgroundColor: trackColor,
                            width: 9,
                            height: 9,
                            top: "-25%",
                            left: "-25%",
                            animation: "ping 1.2s cubic-bezier(0, 0, 0.2, 1) infinite", // slower ping
                          }}
                        />
                        {/* Foreground dot */}
                        <div
                          className="relative rounded-full border border-card shadow-sm"
                          style={{
                            backgroundColor: trackColor,
                            width: 6,
                            height: 6,
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Milestone labels (+ sub-state labels centered under their connector) */}
        <div className="mt-1 flex items-baseline">
          {MILESTONE_LABELS.map((label, idx) => {
            const isLast = idx === MILESTONE_LABELS.length - 1;
            const reached = !isDeclined && progressIndex >= idx;
            const showSubLabel = !isDeclined && !!subLabel && subSegIdx === idx;
            return (
              <React.Fragment key={label}>
                <div style={{ width: CIRCLE_SIZE }} className="flex shrink-0 justify-center">
                  <span
                    className={cn(
                      "whitespace-nowrap text-[9px] font-medium leading-tight",
                      reached ? "text-foreground/75" : "text-muted-foreground/40"
                    )}
                  >
                    {label}
                  </span>
                </div>
                {!isLast && (
                  <div className="flex-1 text-center">
                    {showSubLabel && (
                      <span
                        className="whitespace-nowrap text-[9px] font-semibold leading-tight"
                        style={{ color: trackColor }}
                      >
                        {subLabel}
                      </span>
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StatusFlowbar;
